<template>
  <div class="common-radio-wrapper">
    <el-radio-group class="c-group" v-model="internalValue" @change="handleRadioChange">
      <el-radio :label="-1" size="large">Now</el-radio>
      <el-radio :label="getCombinedValue" size="large">
        <div class="or_in"> or in </div>
      </el-radio>
    </el-radio-group>
    
    <div class="time-picker" v-if="internalValue !== -1">
      <el-input v-model="inputValue" 
                :class="{ 'error': showErrorMessage }" 
                :disabled="isOptionOneSelected"
                placeholder="Enter Time" 
                type="number" 
                @input="handleInput" 
                @keypress="validateKeyPress" />
      <el-select popper-class="normal-select" 
                v-model="selectValue" 
                :disabled="isOptionOneSelected">
        <el-option label="minutes" value="minutes" />
        <el-option label="hours" value="hours" />
        <el-option label="days" value="days" />
      </el-select>
    </div>
    
    <p v-if="showErrorMessage" class="error-message">
      Input value must be greater than 0
    </p>
  </div>
</template>

<script>
import { ref, watch, computed, onMounted, nextTick  } from 'vue';
import { useStore } from "vuex";

export default {
  props: {
    modelValue: {
      type: [String, Number],
      required: true
    }
  },
  emits: ['update:modelValue'],
  
  setup(props, { emit }) {
    const $store = useStore();
    const userRootData = $store.getters.GET_USER_DATA;
    const subscription = ref(
      userRootData.calculatedSubscriptions.subscription 
        ? userRootData.calculatedSubscriptions.subscription 
        : false
    );
    
    const internalValue = ref(props.modelValue);
    const inputValue = ref('');
    const selectValue = ref('minutes');
    const showErrorMessage = ref(false);

    const isOptionOneSelected = computed(() => internalValue.value === -1);

    const handleInput = () => {
      const sanitizedInput = sanitizeInput(inputValue.value);
      inputValue.value = sanitizedInput;
      showErrorMessage.value = sanitizedInput.length === 0 || parseInt(sanitizedInput) <= 0;
    };

    const validateKeyPress = (event) => {
      const charCode = event.charCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    };

    const sanitizeInput = (input) => {
      return input.replace(/[^0-9]/g, '');
    };

    const timeUnitToHoursMapping = {
      minutes: 1 / 60,
      hours: 1,
      days: 24
    };

    const getTotalHours = () => {
      const inputValueNumber = parseFloat(inputValue.value);
      if (isNaN(inputValueNumber)) {
        return 0;
      }
      const selectValueFactor = timeUnitToHoursMapping[selectValue.value];
      return inputValueNumber * selectValueFactor;
    };

    const getCombinedValue = computed(() => {
      return getTotalHours();
    });

    const handleRadioChange = () => {
      if (!isOptionOneSelected.value) {
        internalValue.value = getCombinedValue.value;
        if (internalValue.value == 0) {
          showErrorMessage.value = true;
        }
      } else {
        internalValue.value = -1;
        showErrorMessage.value = false;
      }
      emit('update:modelValue', internalValue.value);
    };

    // watch(() => props.modelValue, (newVal) => {
    //   internalValue.value = newVal;
    //   recalculateValues();
    // });

    watch(inputValue, () => {
      if (!isOptionOneSelected.value) {
        internalValue.value = inputValue.value === 0 ? -1 : getCombinedValue.value;
      } else {
        internalValue.value = -1;
      }
      emit('update:modelValue', internalValue.value);
    });

    watch(selectValue, () => {
      if (!isOptionOneSelected.value) {
        internalValue.value = getCombinedValue.value;
      } else {
        internalValue.value = -1;
      }
      emit('update:modelValue', internalValue.value);
    });

    function getSelectValueFromInputValue(inputValue) {
      const numericValue = parseFloat(inputValue); // Convert the input value to a numeric value
      if (Number.isInteger(numericValue) && numericValue % 24 == 0) {
        return 'days';
      } else if (Number.isInteger(numericValue) && numericValue >= 1) {
        return 'hours';
      } else {
        return 'minutes';
      }
    }

    function recalculateValues() {
      if (props.modelValue === 0) {
        inputValue.value = '0';
        return;
      }

      if (props.modelValue !== -1 && props.modelValue && !isNaN(parseFloat(props.modelValue))) {
        const inputValueNumber = Number(parseFloat(props.modelValue));
        if (!Number.isNaN(inputValueNumber)) {
          selectValue.value = getSelectValueFromInputValue(inputValueNumber);
          switch (selectValue.value) {
            case 'days':
              inputValue.value = (inputValueNumber / 24).toString();
              break;
            case 'minutes':
              inputValue.value = Math.round(inputValueNumber * 60).toString();
              break;
            case 'hours':
            default:
              inputValue.value = inputValueNumber.toString();
              break;
          }
        }
      }
    }
    

    onMounted(async () => {
      recalculateValues();

      if (props.modelValue === null || props.modelValue === undefined || props.modelValue === 0) {
        await nextTick(); // wait for DOM
        internalValue.value = -1;
        emit('update:modelValue', -1);
      } else {
        internalValue.value = props.modelValue;
      }
    });

    return {
      internalValue,
      inputValue,
      selectValue,
      isOptionOneSelected,
      getCombinedValue,
      showErrorMessage,
      handleInput,
      validateKeyPress,
      handleRadioChange,
      subscription
    };
  }
};
</script>

<style scoped>
.common-radio-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.c-group {
  display: flex;
  gap: 16px;
}

.time-picker {
  display: flex;
  gap: 8px;
  align-items: center;
}

.or_in {
  margin-left: 4px;
}

.error-message {
  color: red;
  font-size: 14px;
  line-height: 1;
  margin-block: 4px 0;
}

.error :deep(.el-input__inner) {
  border-color: red;
}
</style>